import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[appNumberFormatNoSum]',
  standalone: true,
})
export class NumberFormatDirectiveNoSum {
  @Input() isNonEditable: boolean = false;

  private el: HTMLInputElement;

  constructor(private elementRef: ElementRef) {
    this.el = this.elementRef.nativeElement;
  }

  @HostListener('input', ['$event']) onInputChange(event: Event): void {
    if (this.isNonEditable) return;

    const input = event.target as HTMLInputElement;
    const originalValue = input.value;
    const cursorPosition = input.selectionStart || 0;

    // Remove all formatting to get raw number
    let unformattedValue = originalValue.replace(/(?!^-)[^0-9+\-]/g, '').replace(/^-0+/, '-').replace(/^0+/, '');

    // Allow 0 only if it's a single digit
    if (unformattedValue === '0' || unformattedValue === '') {
      this.el.value = '0';
      return;
    }

    const MIN = -9223372036854775807;
    const MAX = 9223372036854775807;
    let numValue = parseInt(unformattedValue, 10);
    if (!isNaN(numValue)) {
      if (numValue < MIN) numValue = MIN;
      if (numValue > MAX) numValue = MAX;
      unformattedValue = numValue.toString();
    }

    // Capture character before cursor in raw value
    const rawBeforeCursor = originalValue.slice(0, cursorPosition);
    const unformattedBeforeCursor = rawBeforeCursor.replace(/(?!^-)[^0-9+\-]/g, '');

    const caretIndexInRaw = unformattedBeforeCursor.length;

    // Format the value
    const formattedValue = this.formatNumber(unformattedValue);

    this.el.value = formattedValue;

    // Now map raw caret index to formatted string
    let formattedIndex = 0, rawIndex = 0;
    while (formattedIndex < formattedValue.length && rawIndex < caretIndexInRaw) {
      const char = formattedValue[formattedIndex];
      if (/\d/.test(char)) {
        rawIndex++;
      }
      formattedIndex++;
    }

    // Ensure cursor is within bounds
    formattedIndex = Math.max(0, Math.min(formattedValue.length, formattedIndex));
    input.setSelectionRange(formattedIndex, formattedIndex);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  @HostListener('keydown.enter', ['$event']) onEnterPress(event: KeyboardEvent): void {
    this.formatCurrentInput(event);
    this.focusNextInput(event);
  }

  @HostListener('keydown.tab', ['$event']) onTabPress(event: KeyboardEvent): void {
    this.formatCurrentInput(event);
  }

  @HostListener('keydown.arrowup', ['$event']) onArrowUp(event: KeyboardEvent): void {
    this.formatCurrentInput(event);
    this.focusPrevInput(event);
  }

  @HostListener('keydown.arrowdown', ['$event']) onArrowDown(event: KeyboardEvent): void {
    this.formatCurrentInput(event);
    this.focusNextInput(event);
  }

  @HostListener('blur', ['$event']) onBlur(event: FocusEvent): void {
    this.formatCurrentInput(event);
  }

  @Input() set formattedValue(value: number | string) {
    if (value != null) {
      const formatted = this.formatNumber(value.toString());
      this.el.value = formatted;
    }
  }

  private formatNumber(value: string): string {
    // Remove all non-digit except minus sign at start
    value = value.replace(/(?!^-)[^0-9]/g, '');
    // Handle negative sign
    const isNegative = value.startsWith('-') || value.startsWith('–');
    let num = value.replace(/[^0-9]/g, '');
    if (num === '') return isNegative ? '-' : '';
    // Add thousand separators
    num = num.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return isNegative ? '-' + num : num;
  }

  private formatCurrentInput(event: Event | KeyboardEvent): void {
    const input = (event.target as HTMLInputElement);
    const value = input.value.trim();
    // Only format the current number, keep minus sign if present
    let formatted = this.formatNumber(value);
    this.el.value = formatted;
    input.dispatchEvent(new Event('input'));
  }

  private focusNextInput(event: Event | KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const form = input.form;
    if (form) {
      const inputs = Array.from(form.querySelectorAll('input'));
      const currentIndex = inputs.indexOf(input);
      for (let i = currentIndex + 1; i < inputs.length; i++) {
        const nextInput = inputs[i] as HTMLInputElement;
        if (!nextInput.disabled) {
          nextInput.focus();
          break;
        }
      }
    }
  }

  private focusPrevInput(event: Event | KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const form = input.form;
    if (form) {
      const inputs = Array.from(form.querySelectorAll('input'));
      const currentIndex = inputs.indexOf(input);
      for (let i = currentIndex - 1; i >= 0; i--) {
        const prevInput = inputs[i] as HTMLInputElement;
        if (!prevInput.disabled) {
          prevInput.focus();
          break;
        }
      }
    }
  }
}
